// Emergency Numbers (911) — the Portal (UC Platform) vs Bandwidth report, the
// unregistered-number alerts and the Static ↔ DLR sync with manager approval.
//
// Overview · connections, last scan, status counts, start a scan
// Domains  · every assessed domain, its plan, blockers, decision and approval
// Numbers  · number-level report: who serves 911 for each emergency number
// Alerts   · numbers with no registration at Bandwidth, and the digest
// Runs     · scans and fixes with their plain-English log
//
// Settings (platforms, hybrid list, auto-fix switch, recipients) live in
// Settings → Emergency Numbers.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, Building2, Hash, LayoutDashboard, RefreshCw, ScrollText, Settings2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Overview from "./e911/Overview";
import Domains from "./e911/Domains";
import Numbers from "./e911/Numbers";
import Alerts from "./e911/Alerts";
import Runs from "./e911/Runs";
import DomainDrawer from "./e911/DomainDrawer";

const TABS = ["overview", "domains", "numbers", "alerts", "runs"];

export default function EmergencyNumbers() {
  const { has } = useAuth();
  const canEdit = has("e911.edit");
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "overview";
  const [overview, setOverview] = useState(null);
  const [domainFilter, setDomainFilter] = useState({ status: params.get("tab") === "domains" ? params.get("status") || "" : "" });
  const [numberFilter, setNumberFilter] = useState({ status: params.get("tab") === "numbers" ? params.get("status") || "" : "" });
  const [runFocus, setRunFocus] = useState("");
  const [open, setOpen] = useState(null); // { platform, domain }
  const [bump, setBump] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const wasRunning = useRef(false);

  const setTab = useCallback((next, status) => {
    setParams((p) => {
      const n = new URLSearchParams(p);
      n.set("tab", next);
      if (status) n.set("status", status); else n.delete("status");
      return n;
    }, { replace: true });
  }, [setParams]);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/e911/overview");
      setOverview(data);
      const running = !!data.running;
      if (wasRunning.current && !running) setBump((b) => b + 1);
      wasRunning.current = running;
    } catch {
      toast.error("Could not load the Emergency Numbers overview");
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  // Iter 309 — the header Refresh used to reload only the counts; the open tab
  // kept its stale table. Now it re-queries the overview AND every tab.
  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    try {
      await loadOverview();
      setBump((b) => b + 1);
      toast.success("Refreshed");
    } finally {
      setRefreshing(false);
    }
  }, [loadOverview]);
  useEffect(() => {
    if (!overview?.running) return undefined;
    const t = setInterval(loadOverview, 4000);
    return () => clearInterval(t);
  }, [overview?.running, loadOverview]);

  const goto = useCallback((next, extra = {}) => {
    if (next === "domains") setDomainFilter({ status: extra.status || "" });
    if (next === "numbers") setNumberFilter({ status: extra.status || "" });
    if (next === "runs") setRunFocus(extra.run_id || "");
    setTab(next, next === "domains" || next === "numbers" ? extra.status : "");
  }, [setTab]);

  const d = overview?.domains || {};
  const attention = (d.blocked || 0) + (d.needs_work || 0) + (d.review || 0);
  const n = overview?.numbers || {};
  const unregistered = (n.not_registered || 0) + (n.wrong || 0);

  return (
    <div className="px-1" data-testid="e911-page">
      <PageHeader title="Emergency Numbers" testid="e911-header"
                  subtitle="911 — what the Portal says versus what Bandwidth holds, per domain and per number. Nothing changes without a manager's approval.">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={refreshAll} disabled={refreshing} data-testid="e911-refresh">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
          {canEdit && (
            <Link to="/settings?tab=e911" data-testid="e911-settings-link">
              <Button size="sm" variant="outline"><Settings2 className="h-4 w-4" /> Settings</Button>
            </Link>
          )}
        </div>
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="e911-tabs">
          <TabsTrigger value="overview" data-testid="e911-tab-overview">
            <LayoutDashboard className="mr-1.5 h-4 w-4" /> Overview
          </TabsTrigger>
          <TabsTrigger value="domains" data-testid="e911-tab-domains">
            <Building2 className="mr-1.5 h-4 w-4" /> Domains
            {!!attention && (
              <span className="ml-1.5 rounded bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-800"
                    data-testid="e911-domains-badge">{attention}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="numbers" data-testid="e911-tab-numbers">
            <Hash className="mr-1.5 h-4 w-4" /> Numbers
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="e911-tab-alerts">
            <BellRing className="mr-1.5 h-4 w-4" /> Alerts
            {!!unregistered && (
              <span className="ml-1.5 rounded bg-rose-100 px-1.5 text-[10px] font-semibold text-rose-800"
                    data-testid="e911-alerts-badge">{unregistered}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="runs" data-testid="e911-tab-runs">
            <ScrollText className="mr-1.5 h-4 w-4" /> Runs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Overview data={overview} canEdit={canEdit} onGoto={goto} onStarted={loadOverview} />
        </TabsContent>
        <TabsContent value="domains" className="mt-4">
          <Domains overview={overview} filter={domainFilter} refreshKey={bump} onOpen={setOpen} />
        </TabsContent>
        <TabsContent value="numbers" className="mt-4">
          <Numbers overview={overview} canEdit={canEdit} filter={numberFilter} refreshKey={bump} onOpenDomain={setOpen} onStarted={loadOverview} />
        </TabsContent>
        <TabsContent value="alerts" className="mt-4">
          <Alerts canEdit={canEdit} refreshKey={bump} onOpenDomain={setOpen} />
        </TabsContent>
        <TabsContent value="runs" className="mt-4">
          <Runs focus={runFocus} refreshKey={bump} running={overview?.running} />
        </TabsContent>
      </Tabs>

      <DomainDrawer target={open} canEdit={canEdit} onClose={() => setOpen(null)}
                    onChanged={() => { setBump((b) => b + 1); loadOverview(); }}
                    onShowRun={(runId) => { setOpen(null); goto("runs", { run_id: runId }); }} />
    </div>
  );
}
